import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";

const BASE_URL = process.env.REACT_APP_BASE_URL;

export default function AdminRequestAccept() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);

  const [searchText, setSearchText] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");

  const role = localStorage.getItem("role");
  const token = localStorage.getItem(`${role}Token`);

  /* FETCH REQUESTS */
  const fetchRequests = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URL}/api/duty/decision/all`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (Array.isArray(res.data)) setRequests(res.data);
      else if (Array.isArray(res.data?.data)) setRequests(res.data.data);
    } catch (error) {
      toast.error("Failed to load requests");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  /* ACCEPT / REJECT */
  const handleDecision = async (item, action) => {
    try {
      const finalStatus =
        role === "admin"
          ? action === "accept"
            ? "admin_accepted"
            : "admin_rejected"
          : action === "accept"
            ? "manager_accepted"
            : "manager_rejected";

      const payload = {
        id: item.id,
        status: finalStatus,
        reason: "Deviation",
        forceapply: "True",
      };

      await axios.post(`${BASE_URL}/api/duty/decision/management`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success(finalStatus.replace("_", " ").toUpperCase());
      fetchRequests();
    } catch (error) {
      toast.error("Action Failed");
    }
  };

  /* FILTERING */
  const filteredData = requests.filter((item) => {
    const text = searchText.trim().toLowerCase();
    const searchMatch = text
      ? [
          item.officer?.name,
          item.officer?.rank,
          item.status,
          item.message,
        ].some((field) =>
          String(field || "").toLowerCase().includes(text)
        )
      : true;

    const statusMatch = selectedStatus
      ? String(item.status || "")
          .toLowerCase()
          .includes(selectedStatus.toLowerCase())
      : true;

    return searchMatch && statusMatch;
  });

  return (
    <>
      <div className="request-approval-wrapper">
        {/* Header */}
        <header className="page-header">
          <h1 className="page-title">Request Approval</h1>
          <div className="total-card">
            <span className="total-count">{filteredData.length}</span>
            <span className="total-label">Pending Requests</span>
          </div>
        </header>

        {/* Filters */}
        <section className="filters-section">
          <div className="filter-group search-group">
            <label>Search</label>
            <div className="search-input-wrapper">
              <i className="fas fa-search"></i>
              <input
                type="text"
                placeholder="Officer name, rank, message..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </div>
          </div>

          <div className="filter-group">
            <label>Status Filter</label>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
            >
              <option value="">All Status</option>
              <option value="guard rejected">Officer Rejected</option>
              <option value="manager accepted">Manager Accepted</option>
              <option value="manager rejected">Manager Rejected</option>
              <option value="admin accepted">Admin Accepted</option>
              <option value="admin rejected">Admin Rejected</option>
            </select>
          </div>
        </section>

        {/* Table / Cards */}
        <section className="table-section">
          {loading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading requests...</p>
            </div>
          ) : filteredData.length === 0 ? (
            <div className="empty-state">
              <i className="fas fa-clipboard-check"></i>
              <h3>No Requests Found</h3>
              <p>All requests have been processed or none are pending.</p>
            </div>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="table-container desktop-only">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Officer Name</th>
                      <th>Rank</th>
                      <th>Message</th>
                      <th>Status</th>
                      <th>Request Time</th>
                      <th>Response Time</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredData.map((item, index) => {
                      const statusText = item.status || "Completed";
                      const isGuardRejected =
                        String(statusText).toLowerCase().trim() === "guard rejected";

                      const getStatusClass = () => {
                        if (statusText.toLowerCase().includes("accepted")) return "active";
                        if (statusText.toLowerCase().includes("rejected")) return "rejected";
                        return "pending";
                      };

                      return (
                        <tr key={item.id} className="table-row">
                          <td>{index + 1}</td>
                          <td className="name-cell">{item.officer?.name || "—"}</td>
                          <td>
                            <span className="badge">{item.officer?.rank || "—"}</span>
                          </td>
                          <td>{item.message || "No message"}</td>
                          <td>
                            <span className={`status-badge ${getStatusClass()}`}>
                              {statusText}
                            </span>
                          </td>
                          <td>
                            {item.requestTime
                              ? new Date(item.requestTime.split(".")[0]).toLocaleString("en-IN")
                              : "—"}
                          </td>
                          <td>
                            {item.responseTime
                              ? new Date(item.responseTime.split(".")[0]).toLocaleString("en-IN")
                              : "—"}
                          </td>
                          <td className="actions-cell">
                            {isGuardRejected ? (
                              <>
                                <button
                                  className="action-btn approve"
                                  onClick={() => handleDecision(item, "accept")}
                                >
                                  <i className="fas fa-check"></i> Accept
                                </button>
                                <button
                                  className="action-btn reject"
                                  onClick={() => handleDecision(item, "reject")}
                                >
                                  <i className="fas fa-times"></i> Reject
                                </button>
                              </>
                            ) : (
                              <span className="completed-text">Completed</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="mobile-cards mobile-only">
                {filteredData.map((item, index) => {
                  const statusText = item.status || "Completed";
                  const isGuardRejected =
                    String(statusText).toLowerCase().trim() === "guard rejected";

                  const getStatusClass = () => {
                    if (statusText.toLowerCase().includes("accepted")) return "active";
                    if (statusText.toLowerCase().includes("rejected")) return "rejected";
                    return "pending";
                  };

                  return (
                    <div key={item.id} className="request-card">
                      <div className="card-header">
                        <span className="index">#{index + 1}</span>
                        <span className={`status-badge ${getStatusClass()}`}>
                          {statusText}
                        </span>
                      </div>
                      <h3 className="officer-name">{item.officer?.name || "Unknown Officer"}</h3>
                      <div className="card-details">
                        <div><strong>Rank:</strong> <span className="badge">{item.officer?.rank || "—"}</span></div>
                        <div><strong>Message:</strong> {item.message || "No message"}</div>
                        <div><strong>Request:</strong> {item.requestTime ? new Date(item.requestTime.split(".")[0]).toLocaleString("en-IN") : "—"}</div>
                        <div><strong>Response:</strong> {item.responseTime ? new Date(item.responseTime.split(".")[0]).toLocaleString("en-IN") : "—"}</div>
                      </div>
                      {isGuardRejected && (
                        <div className="card-actions">
                          <button
                            className="action-btn approve"
                            onClick={() => handleDecision(item, "accept")}
                          >
                            <i className="fas fa-check"></i> Accept
                          </button>
                          <button
                            className="action-btn reject"
                            onClick={() => handleDecision(item, "reject")}
                          >
                            <i className="fas fa-times"></i> Reject
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </section>
      </div>

      <style jsx>{`
        .request-approval-wrapper {
          min-height: 100vh;
          background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
          color: #e2e8f0;
          padding: 20px;
        }

        .page-header {
          margin-bottom: 30px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 20px;
        }

        .page-title {
          font-size: 2rem;
          font-weight: 700;
          color: #60a5fa;
        }

        .total-card {
          background: rgba(59, 130, 246, 0.2);
          border: 1px solid rgba(59, 130, 246, 0.4);
          padding: 16px 24px;
          border-radius: 16px;
          text-align: center;
        }

        .total-count {
          display: block;
          font-size: 1.8rem;
          font-weight: 800;
          color: #60a5fa;
        }

        .total-label {
          font-size: 0.95rem;
          opacity: 0.9;
        }

        .filters-section {
          display: flex;
          flex-wrap: wrap;
          gap: 20px;
          margin-bottom: 30px;
        }

        .filter-group {
          flex: 1;
          min-width: 250px;
          display: flex;
          flex-direction: column;
        }

        .filter-group label {
          font-weight: 600;
          margin-bottom: 8px;
          color: white;
        }

        .search-input-wrapper {
          position: relative;
        }

        .search-input-wrapper i {
          position: absolute;
          left: 18px;
          top: 50%;
          transform: translateY(-50%);
          color: #94a3b8;
        }

        .search-input-wrapper input,
        .filter-group select {
          width: 100%;
          padding: 16px 20px;
          padding-left: 52px; /* for search icon */
          background: rgba(15, 23, 42, 0.6);
          border: 2px solid rgba(59, 130, 246, 0.4);
          border-radius: 16px;
          color: white;
          font-size: 1rem;
        }

        .filter-group select {
          padding-left: 20px;
        }

        .search-input-wrapper input:focus,
        .filter-group select:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.3);
        }

        .table-section {
          background: rgba(30, 41, 59, 0.8);
          backdrop-filter: blur(12px);
          border-radius: 20px;
          padding: 24px;
          border: 1px solid rgba(59, 130, 246, 0.3);
        }

        .loading-state,
        .empty-state {
          text-align: center;
          padding: 60px 20px;
          color: #94a3b8;
        }

        .spinner {
          width: 50px;
          height: 50px;
          border: 4px solid #1e293b;
          border-top: 4px solid #60a5fa;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 20px;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .empty-state i {
          font-size: 4rem;
          opacity: 0.4;
          margin-bottom: 20px;
        }

        .empty-state h3 {
          font-size: 1.5rem;
          margin-bottom: 10px;
          color: white;
        }

        /* Desktop Table */
        .data-table {
          width: 100%;
          border-collapse: collapse;
        }

        .data-table th {
          background: rgba(59, 130, 246, 0.15);
          padding: 16px;
          text-align: left;
          font-weight: 600;
          color: #60a5fa;
          font-size: 0.95rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .table-row {
          transition: all 0.3s ease;
        }

        .table-row:hover {
          background: rgba(59, 130, 246, 0.1);
        }

        .table-row td {
          padding: 18px 16px;
          border-bottom: 1px solid rgba(100, 116, 139, 0.3);
        }

        .name-cell {
          font-weight: 600;
          color: white;
        }

        .badge {
          background: rgba(59, 130, 246, 0.2);
          color: #60a5fa;
          padding: 8px 16px;
          border-radius: 20px;
          font-weight: 600;
          font-size: 0.9rem;
        }

        .status-badge {
          padding: 8px 16px;
          border-radius: 20px;
          font-weight: 600;
          font-size: 0.9rem;
        }

        .status-badge.active {
          background: rgba(16, 185, 129, 0.2);
          color: #10b981;
        }

        .status-badge.rejected {
          background: rgba(239, 68, 68, 0.2);
          color: #ef4444;
        }

        .status-badge.pending {
          background: rgba(255, 193, 7, 0.2);
          color: #f59e0b;
        }

        .actions-cell {
          display: flex;
          gap: 12px;
        }

        .action-btn {
          padding: 10px 16px;
          border: none;
          border-radius: 10px;
          cursor: pointer;
          font-weight: 600;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .action-btn.approve {
          background: #10b981;
          color: white;
        }

        .action-btn.approve:hover {
          background: #059669;
          transform: translateY(-2px);
        }

        .action-btn.reject {
          background: #ef4444;
          color: white;
        }

        .action-btn.reject:hover {
          background: #dc2626;
          transform: translateY(-2px);
        }

        .completed-text {
          color: #94a3b8;
          font-style: italic;
        }

        /* Mobile Cards */
        .mobile-cards {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .request-card {
          background: rgba(15, 23, 42, 0.6);
          border: 1px solid rgba(59, 130, 246, 0.3);
          border-radius: 16px;
          padding: 20px;
          transition: all 0.3s ease;
        }

        .card-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
        }

        .index {
          font-weight: 600;
          color: #60a5fa;
        }

        .officer-name {
          font-size: 1.3rem;
          font-weight: 700;
          color: white;
          margin-bottom: 12px;
        }

        .card-details {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 10px;
          margin-bottom: 16px;
          font-size: 0.95rem;
        }

        .card-details div strong {
          color: #60a5fa;
        }

        .card-actions {
          display: flex;
          gap: 12px;
          margin-top: 16px;
        }

        /* Responsive */
        @media (max-width: 991px) {
          .desktop-only { display: none; }
          .mobile-only { display: flex; }
          .card-details { grid-template-columns: 1fr; }
        }

        @media (min-width: 992px) {
          .mobile-only { display: none; }
          .desktop-only { display: block; }
        }

        @media (max-width: 768px) {
          .page-header {
            flex-direction: column;
            align-items: flex-start;
          }

          .filters-section {
            flex-direction: column;
          }

          .page-title {
            font-size: 1.8rem;
          }
        }
      `}</style>
    </>
  );
}




// //------------------------------------------3------------------------------------------------

// import React, { useEffect, useState } from "react";
// import axios from "axios";
// import { toast } from "react-toastify";

// const BASE_URL = process.env.REACT_APP_BASE_URL;

// export default function AdminRequestAccept() {
//   const [requests, setRequests] = useState([]);
//   const [loading, setLoading] = useState(false);

//   // SEARCH & FILTER
//   const [searchText, setSearchText] = useState("");
//   const [selectedStatus, setSelectedStatus] = useState("");

//   // ROLE & TOKEN
//   const role = localStorage.getItem("role");
//   const token = localStorage.getItem(`${role}Token`);

//   /* ---------------- FETCH ---------------- */
//   const fetchRequests = async () => {
//     try {
//       setLoading(true);

//       const res = await axios.get(`${BASE_URL}/api/duty/decision/all`, {
//         headers: { Authorization: `Bearer ${token}` },
//       });

//       if (Array.isArray(res.data)) setRequests(res.data);
//       else if (Array.isArray(res.data?.data)) setRequests(res.data.data);
//     } catch (error) {
//       toast.error("Failed to load requests");
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     fetchRequests();
//   }, []);

//   /* ---------------- ACCEPT / REJECT ---------------- */
//   const handleDecision = async (item, action) => {
//     try {
//       const finalStatus =
//         role === "admin"
//           ? action === "accept"
//             ? "admin_accepted"
//             : "admin_rejected"
//           : action === "accept"
//             ? "manager_accepted"
//             : "manager_rejected";

//       const payload = {
//         id: item.id,
//         status: finalStatus,
//         reason:"Deviation",
//         forceapply: "True"
//       };

//       await axios.post(`${BASE_URL}/api/duty/decision/management`, payload, {
//         headers: { Authorization: `Bearer ${token}` },
//       });

//       toast.success(finalStatus);
//       fetchRequests();
//     } catch (error) {
//       toast.error("Action Failed");
//     }
//   };

//   /* ---------------- SEARCH + STATUS FILTER ---------------- */
//   const filteredData = requests.filter((item) => {
//     const text = searchText.trim().toLowerCase();

//     const searchMatch = text
//       ? [
//         item.officer?.name,
//         item.officer?.rank,
//         item.status,
//         item.message,
//       ].some((field) =>
//         String(field || "").toLowerCase().includes(text)
//       )
//       : true;

//     const statusMatch = selectedStatus
//       ? String(item.status || "").toLowerCase().includes(
//         selectedStatus.toLowerCase()
//       )
//       : true;

//     return searchMatch && statusMatch;
//   });

//   /*  OPTIONAL SORT */
//   const sortedData = [...filteredData];

//   return (
//     <div style={styles.container}>
//       <h2 style={styles.title}>Admin / Manager Request Approval</h2>

//       {/* FILTERS */}
//       <div style={styles.filterRow}>
//         <div style={styles.filterCard}>
//           <label style={styles.filterLabel}>Search</label>
//           <input
//             type="text"
//             value={searchText}
//             onChange={(e) => setSearchText(e.target.value)}
//             placeholder="Search by name, rank, message..."
//             style={styles.input}
//           />
//         </div>

//         <div style={styles.filterCard}>
//           <label style={styles.filterLabel}>Status</label>
//           <select
//             value={selectedStatus}
//             onChange={(e) => setSelectedStatus(e.target.value)}
//             style={styles.select}
//           >
//             <option value="">All</option>
//             <option value="admin accepted">Admin Accepted</option>
//             <option value="admin rejected">Admin Rejected</option>
//             <option value="manager accepted">Manager Accepted</option>
//             <option value="manager rejected">Manager Rejected</option>
//             <option value="guard rejected">Officer Rejected</option>
//           </select>
//         </div>
//       </div>

//       {/* TABLE */}
//       <div style={styles.card}>
//         {loading ? (
//           <div style={styles.noData}>Loading...</div>
//         ) : sortedData.length === 0 ? (
//           <div style={styles.noData}>No Requests Found</div>
//         ) : (
//           <table style={styles.table}>
//             <thead>
//               <tr>
//                 <th style={styles.th}>#</th>
//                 <th style={styles.th}>Officer</th>
//                 <th style={styles.th}>Rank</th>
//                 <th style={styles.th}>Message</th>
//                 <th style={styles.th}>Status</th>
//                 <th style={styles.th}>Request Time</th>
//                 <th style={styles.th}>Response Time</th>
//                 <th style={styles.th}>Action</th>
//               </tr>
//             </thead>

//             <tbody>
//               {sortedData.map((item, index) => {
//                 const statusText = item.status || "Completed";

//                 //  ONLY show buttons if status === "Guard Rejected"
//                 const isGuardRejected =
//                   String(statusText).toLowerCase().trim() ===
//                   "guard rejected";

//                 const statusStyle =
//                   statusText.toLowerCase().includes("accepted")
//                     ? styles.statusActive
//                     : statusText.toLowerCase().includes("rejected")
//                       ? styles.statusInactive
//                       : styles.statusOther;

//                 return (
//                   <tr key={item.id} style={styles.row}>
//                     <td style={styles.td}>{index + 1}</td>
//                     <td style={styles.td}>{item.officer?.name}</td>
//                     <td style={styles.td}>{item.officer?.rank}</td>
//                     <td style={styles.td}>{item.message || "N/A"}</td>

//                     <td style={styles.td}>
//                       <span style={statusStyle}>{statusText}</span>
//                     </td>
//                     <td style={styles.td}>
//                       {item.requestTime
//                         ? new Date(item.requestTime.split(".")[0]).toLocaleString("en-IN")
//                         : "N/A"}
//                     </td>

//                     <td style={styles.td}>
//                       {item.responseTime
//                         ? new Date(item.responseTime.split(".")[0]).toLocaleString("en-IN")
//                         : "N/A"}
//                     </td>


//                     <td style={styles.td}>
//                       {isGuardRejected ? (
//                         <div style={{ display: "flex", gap: 10 }}>
//                           <button
//                             style={styles.acceptBtn}
//                             onClick={() =>
//                               handleDecision(item, "accept")
//                             }
//                           >
//                             Accept
//                           </button>

//                           <button
//                             style={styles.rejectBtn}
//                             onClick={() =>
//                               handleDecision(item, "reject")
//                             }
//                           >
//                             Reject
//                           </button>
//                         </div>
//                       ) : (
//                         <span style={{ color: "#777", fontWeight: 600 }}>
//                           Completed
//                         </span>
//                       )}
//                     </td>
//                   </tr>
//                 );
//               })}
//             </tbody>
//           </table>
//         )}
//       </div>
//     </div>
//   );
// }

// /* ---------------- STYLES ---------------- */

// const styles = {
//   container: { padding: 30, background: "linear-gradient(135deg, rgba(190,235,120,0.45), rgba(120,200,255,0.45))  " },
//   title: { fontSize: 30, fontWeight: 800, marginBottom: 25, color: "#1967d2" },

//   filterRow: { display: "flex", gap: 20, marginBottom: 25 },
//   filterCard: {
//     flex: 1,
//     padding: 18,
//     borderRadius: 12,
//     background: "rgba(255,255,255,0.45)",
//     boxShadow: "0 3px 12px rgba(0,0,0,0.08)",
//   },
//   filterLabel: { fontSize: 15, fontWeight: 600, marginBottom: 6 },

//   select: {
//     width: "100%",
//     padding: "12px",
//     borderRadius: 8,
//     border: "1.5px solid #1a73e8",
//     fontSize: 15,
//   },

//   input: {
//     width: "100%",
//     padding: 12,
//     borderRadius: 8,
//     border: "1.5px solid #1a73e8",
//     fontSize: 15,
//     outline: "none",
//   },

//   card: {
//     background: "rgba(255,255,255,0.7)",
//     padding: 10,
//     borderRadius: 15,
//     boxShadow: "0 6px 25px rgba(0,0,0,0.1)",
//   },

//   table: { width: "100%", borderCollapse: "collapse" },
//   th: {
//     background: "#b8b8f3ff",
//     padding: "16px 10px",
//     textAlign: "left",
//     borderBottom: "2px solid #ddd",
//     fontWeight: 700,
//   },
//   row: { height: 62, transition: "0.25s" },
//   td: { padding: "14px 10px", borderBottom: "1px solid #eee" },

//   statusActive: {
//     background: "#e6ffe6",
//     padding: "6px 12px",
//     borderRadius: 20,
//     color: "#2e7d32",
//     fontWeight: 600,
//   },
//   statusInactive: {
//     background: "#ffe6e6",
//     padding: "6px 12px",
//     borderRadius: 20,
//     color: "#d32f2f",
//     fontWeight: 600,
//   },
//   statusOther: {
//     background: "#fff8e1",
//     padding: "6px 12px",
//     borderRadius: 20,
//     color: "#ff9800",
//     fontWeight: 600,
//   },

//   acceptBtn: {
//     background: "#2e7d32",
//     color: "#fff",
//     border: "none",
//     padding: "7px 14px",
//     borderRadius: 8,
//     cursor: "pointer",
//   },
//   rejectBtn: {
//     background: "#d32f2f",
//     color: "#fff",
//     border: "none",
//     padding: "7px 14px",
//     borderRadius: 8,
//     cursor: "pointer",
//   },

//   noData: { textAlign: "center", padding: 40, fontSize: 18, color: "#777" },
// };
